import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, X, SlidersHorizontal } from "lucide-react";
import { api } from "@/lib/api";
import { BookCard, EmptyState } from "@/components/cards";

export default function Discover() {
  const [sp] = useSearchParams();
  const [genres, setGenres] = useState([]);
  const [tropes, setTropes] = useState([]);
  const [books, setBooks] = useState([]);
  const [q, setQ] = useState("");
  const [selGenres, setSelGenres] = useState([]);
  const [selTropes, setSelTropes] = useState([]);
  const [status, setStatus] = useState("");
  const [length, setLength] = useState("");
  const [trending, setTrending] = useState(sp.get("trending") === "1");
  const [sort, setSort] = useState("new");

  useEffect(() => {
    api.get("/genres").then((r) => setGenres(r.data));
    api.get("/tropes").then((r) => setTropes(r.data));
  }, []);

  useEffect(() => {
    const p = new URLSearchParams();
    if (q) p.set("q", q);
    if (selGenres.length) p.set("genres", selGenres.join(","));
    if (selTropes.length) p.set("tropes", selTropes.join(","));
    if (length) p.set("length", length);
    if (trending) p.set("trending", "true");
    p.set("sort", sort);
    api.get(`/books?${p.toString()}`).then((r) => setBooks(r.data));
  }, [q, selGenres, selTropes, length, trending, sort]);

  const toggle = (arr, set, v) => set(arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);
  const clear = () => { setQ(""); setSelGenres([]); setSelTropes([]); setStatus(""); setLength(""); setTrending(false); };
  const active = selGenres.length + selTropes.length + (length ? 1 : 0) + (trending ? 1 : 0) + (q ? 1 : 0);

  const Chip = ({ on, onClick, children, testid }) => (
    <button onClick={onClick} data-testid={testid}
      className={`px-3 py-1.5 text-sm rounded-full border transition-colors ${on ? "bg-primary text-primary-foreground border-primary" : "border-border hover:border-primary/60"}`}>
      {children}
    </button>
  );

  return (
    <div className="max-w-[1400px] mx-auto px-4 lg:px-8 py-10 fade-in">
      <h1 className="text-4xl md:text-5xl font-display font-bold">Discover</h1>
      <p className="text-muted-foreground mt-2">Search stories, authors and tropes — filter by exactly what you love.</p>

      <div className="relative mt-8 max-w-3xl">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
        <input data-testid="discover-search" value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="Search stories, authors, tropes..."
          className="w-full bg-secondary/60 border border-border rounded-full pl-12 pr-4 py-4 text-base outline-none focus:border-primary" />
      </div>

      <div className="mt-8 space-y-5">
        <div>
          <p className="track-label text-xs text-muted-foreground mb-3 flex items-center gap-2"><SlidersHorizontal className="h-3.5 w-3.5" /> Genres</p>
          <div className="flex flex-wrap gap-2">
            {genres.map((g) => <Chip key={g.id} testid={`filter-genre-${g.slug}`} on={selGenres.includes(g.slug)} onClick={() => toggle(selGenres, setSelGenres, g.slug)}>{g.name}</Chip>)}
          </div>
        </div>
        <div>
          <p className="track-label text-xs text-muted-foreground mb-3">Tropes</p>
          <div className="flex flex-wrap gap-2">
            {tropes.map((t) => <Chip key={t.id} testid={`filter-trope-${t.slug}`} on={selTropes.includes(t.slug)} onClick={() => toggle(selTropes, setSelTropes, t.slug)}>{t.name}</Chip>)}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="track-label text-xs text-muted-foreground mr-2">Length</span>
          {["short", "medium", "long"].map((l) => <Chip key={l} on={length === l} onClick={() => setLength(length === l ? "" : l)}>{l}</Chip>)}
          <Chip on={trending} onClick={() => setTrending(!trending)} testid="filter-trending">Trending</Chip>
          <select value={sort} onChange={(e) => setSort(e.target.value)} className="ml-auto bg-secondary border border-border rounded-full px-4 py-1.5 text-sm">
            <option value="new">Newest</option>
            <option value="trending">Trending</option>
            <option value="rating">Top Rated</option>
          </select>
          {active > 0 && <button onClick={clear} data-testid="clear-filters" className="flex items-center gap-1 text-sm text-primary"><X className="h-4 w-4" />Clear</button>}
        </div>
      </div>

      <p className="text-sm text-muted-foreground mt-8 mb-4" data-testid="result-count">{books.length} {books.length === 1 ? "story" : "stories"} found</p>
      {books.length ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
          {books.map((b) => <BookCard key={b.id} book={b} />)}
        </div>
      ) : <EmptyState title="No stories match your filters" subtitle="Try removing a filter or clearing your search." />}
    </div>
  );
}
